import { T } from 'gt-react';
import { useQueryClient } from '@tanstack/react-query';
import { getAllowedTaskStatusTransitions } from '@erp/shared-utils';
import type { TaskPriority, TaskStatus } from '@erp/rpc';
import { rpcClient } from 'src/api/rpc/client';
import { invalidateTaskQueries } from 'src/api/rpc/hooks/tasks/invalidateTaskQueries';
import { Card } from 'src/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
} from 'src/components/ui/select';
import {
  PriorityDot,
  StatusPill,
} from 'src/pages/authenticated/inbox/components/helpers';

/**
 * Task detail pieces shared by the inbox task pane and the task card embedded
 * in record views (e.g. the cash receipt detail). Each renders from plain task
 * fields so neither host has to adopt the other's task shape.
 */

/** The task's human-readable id (e.g. "TASK-42") as a mono chip. */
export function TaskDisplayIdChip({ displayId }: { displayId: string }) {
  return (
    <span className="rounded bg-stone-100 px-1.5 py-px font-mono text-[11px] font-medium tabular-nums text-stone-600">
      {displayId}
    </span>
  );
}

/** Priority dot + priority name. */
export function TaskPriorityLabel({ priority }: { priority: TaskPriority }) {
  return (
    <span className="flex items-center gap-1.5 capitalize">
      <PriorityDot priority={priority} />
      {priority}
    </span>
  );
}

/**
 * The status pill as a picker: the current status plus only the transitions
 * the backend state machine permits, so the picker stays in lockstep with
 * TaskService's transition rules.
 */
export function TaskStatusSelect({
  taskId,
  status,
}: {
  taskId: string;
  status: TaskStatus;
}) {
  const queryClient = useQueryClient();
  return (
    <Select
      value={status}
      onValueChange={async (val: TaskStatus) => {
        await rpcClient.tasks.batchUpdate({
          taskIds: [taskId],
          status: val,
        });
        invalidateTaskQueries(queryClient);
      }}
    >
      <SelectTrigger className="h-auto w-auto gap-1 border-none p-0 text-xs shadow-none">
        <StatusPill status={status} />
      </SelectTrigger>
      <SelectContent>
        {[status, ...getAllowedTaskStatusTransitions(status)].map((s) => (
          <SelectItem key={s} value={s}>
            <StatusPill status={s} />
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

function TaskTextBlock({
  label,
  text,
}: {
  label: React.ReactNode;
  text: string;
}) {
  return (
    <div>
      <div className="mb-1.5 text-[11px] font-semibold uppercase tracking-[0.06em] text-stone-500">
        {label}
      </div>
      <Card className="whitespace-pre-wrap p-3 text-[13px] leading-relaxed text-stone-800">
        {text}
      </Card>
    </div>
  );
}

/**
 * The task's free-text summary and the agent's run summary (both written to
 * `metadata`), each as a labeled block. Renders nothing for an absent one.
 */
export function TaskSummaryBlocks({
  summary,
  agentSummary,
}: {
  summary: string | undefined;
  agentSummary: string | undefined;
}) {
  return (
    <>
      {summary && <TaskTextBlock label={<T>Summary</T>} text={summary} />}
      {agentSummary && (
        <TaskTextBlock label={<T>Agent summary</T>} text={agentSummary} />
      )}
    </>
  );
}
